import { Card } from '@design-system';
import { OnboardingLayout, useSlideSetup } from '@/features/client-onboarding';
import { OnboardingCostFactors } from '@/pages/OnboardingCostFactors';
import { OnboardingInsightsSummary } from '@/pages/OnboardingInsightsSummary';

import { PartyPopper, ClipboardList, SlidersHorizontal, TrendingUp } from 'lucide-react';
import Confetti from 'react-confetti';
import { useState, useEffect } from 'react';

const ROADMAP_ITEMS = [
  {
    icon: <ClipboardList className="text-primary size-5" />,
    title: 'Review Your Cost Breakdown',
    description:
      "We'll walk through what's driving your long-term care costs and what contributes to your total.",
  },
  {
    icon: <SlidersHorizontal className="text-primary size-5" />,
    title: 'Choose your care and how your family will likely step in',
    description:
      'Refine your care settings and family involvement to get a more accurate cost figure.',
  },
  {
    icon: <TrendingUp className="text-primary size-5" />,
    title: 'Access funding strategies',
    description:
      'Compare any assets, income, or insurance policies and model how it fits your plan',
  },
];

export function OnboardingD2CPaymentSuccess({ clientId }: ClientIdProps) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  useSlideSetup({
    slidePage: OnboardingD2CPaymentSuccess,
    nextButtonLabel: 'Next',
    nextSlidePage: OnboardingCostFactors,
    previousSlidePage: OnboardingInsightsSummary,
  });

  return (
    <OnboardingLayout>
      {showConfetti && (
        <Confetti
          recycle={false}
          numberOfPieces={300}
          className="fixed! inset-0"
        />
      )}
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center py-6 md:py-16">
        <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
          <PartyPopper className="text-primary size-7" />
        </div>

        <h1 className="mt-6 text-balance text-center text-2xl font-bold text-gray-950 md:text-4xl">
          Finish building your LTC plan
        </h1>
        <p className="text-foreground mt-2 text-balance text-center text-base">
          Here&apos;s what&apos;s next on your roadmap:
        </p>

        <div className="mt-10 w-full space-y-4">
          {ROADMAP_ITEMS.map(item => (
            <Card
              key={item.title}
              className="flex flex-row items-start gap-4 p-4 shadow-none"
            >
              <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
                {item.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-950">{item.title}</h3>
                <p className="text-foreground mt-1 text-sm leading-relaxed">{item.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </OnboardingLayout>
  );
}
